interface CopyParamsOptions {
  sourceUrl?: string; // Required for SSR since window is not available
  blacklist?: string[]; // Parameters to exclude
  additionalParams?: Record<string, string>; // Additional parameters to add
}

/**
 * Copies URL parameters from a source URL to a target URL with optional filtering and additions.
 *
 * This function is designed to work in both browser and SSR environments. It copies query parameters
 * from the source URL to the target URL, with options to blacklist specific parameters and add
 * additional parameters.
 *
 * @param targetUrl - The destination URL where parameters will be added
 * @param options - Configuration options
 * @param options.sourceUrl - The source URL to copy parameters from. Required in SSR environments where window is unavailable
 * @param options.blacklist - Array of parameter names to exclude from copying
 * @param options.additionalParams - Additional parameters to add or overwrite in the target URL
 *
 * @returns The target URL with copied and additional parameters as a string
 *
 * @throws {Error} When running in SSR environment without providing sourceUrl
 *
 * @example
 * // Browser environment - copies from current window.location.href
 * copyParamsToUrl('https://example.com/page', { blacklist: ['token'] })
 *
 * @example
 * // SSR environment - requires sourceUrl
 * copyParamsToUrl('https://example.com/page', {
 *   sourceUrl: 'https://current.com?foo=bar&baz=qux',
 *   blacklist: ['baz'],
 *   additionalParams: { newParam: 'value' }
 * })
 * // Returns: 'https://example.com/page?foo=bar&newParam=value'
 */
export function copyParamsToUrl(targetUrl: string, options: CopyParamsOptions = {}): string {
  const { sourceUrl, blacklist = [], additionalParams = {} } = options;

  // Determine source URL:
  // - If sourceUrl is provided, use it (SSR-safe)
  // - If running in browser, fallback to window.location.href
  // - Otherwise, throw an error for SSR without sourceUrl
  const source = sourceUrl
    ? new URL(sourceUrl)
    : typeof window !== 'undefined'
      ? new URL(window.location.href)
      : (() => {
          throw new Error('sourceUrl is required in SSR environments');
        })();

  // Prepare target URL
  const target = new URL(targetUrl);

  // Copy parameters from source URL except those in the blacklist
  source.searchParams.forEach((value, key) => {
    if (!blacklist.includes(key)) {
      target.searchParams.set(key, value);
    }
  });

  // Add or overwrite additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    target.searchParams.set(key, value);
  });

  return target.toString();
}
